import React, { useEffect, useRef, useState } from 'react';
import Layout from '../components/Layout';
import ScrollReveal from '../components/ScrollReveal';
import { Link } from 'react-router-dom';
import { fetchVisitors, fetchVisitorStats, deleteVisitor as apiDelete, getOwnVisitorId, clearOwnVisitorId, seedVisitors } from '../api/visitorsApi';
import { ChevronDown, ChevronUp, X, Pencil, Loader2 } from 'lucide-react';

function Sparkline({ buckets }) {
  if (!buckets || buckets.length === 0) return null;
  const max = Math.max(1, ...buckets);
  const days = buckets.length;
  const points = buckets.map((b, i) => `${(i / (days - 1)) * 280},${78 - (b / max) * 60}`).join(' ');
  return (
    <svg viewBox="0 0 280 80" className="w-full h-20">
      <polyline fill="url(#g)" stroke="none" points={`0,80 ${points} 280,80`} />
      <polyline fill="none" stroke="#c4757e" strokeWidth="1.5" points={points} />
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#c4757e" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#c4757e" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(2);
    return `${mm}/${dd}/${yy}`;
  } catch (_e) { return ''; }
}

function VisitorCard({ visitor, isOwn, onDelete }) {
  const [menu, setMenu] = useState(false);
  const menuRef = useRef(null);
  const colorValue = visitor.color_value || visitor.colorValue || '#e8a5a8';
  const cardNo = String(visitor.id).slice(-4);

  useEffect(() => {
    if (!menu) return undefined;
    const onDocPointerDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenu(false);
      }
    };
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => document.removeEventListener('pointerdown', onDocPointerDown);
  }, [menu]);

  return (
    <div
      data-platform="true"
      className={`visitor-card relative w-full rounded-[36px] cursor-pointer ${isOwn ? 'ring-2 ring-amber-300/60' : ''}`}
    >
      <div
        className="relative p-6 overflow-hidden text-white"
        style={{ background: colorValue, borderRadius: '36px' }}
      >
        {isOwn && (
          <div ref={menuRef} className="absolute top-3 right-3 flex items-center gap-1.5 z-20">
            <Link to="/onboarding?edit" className="text-white/50 hover:text-white p-1" title="Edit card">
              <Pencil className="w-3.5 h-3.5" />
            </Link>
            <button onClick={() => setMenu(!menu)} className="text-white/50 hover:text-white p-1" aria-label="Card options">
              <X className="w-3.5 h-3.5" />
            </button>
            {menu && (
              <div className="absolute right-0 top-7 bg-[#0f0f10] border border-white/15 rounded-md py-1 z-30 min-w-[110px] shadow-xl">
                <button onClick={() => { onDelete(); setMenu(false); }} className="w-full text-left px-3 py-1.5 text-[10px] uppercase text-white/80 hover:bg-white/5">Delete</button>
              </div>
            )}
          </div>
        )}

        {visitor.signature && (
          <img
            src={visitor.signature}
            alt=""
            className="absolute inset-0 w-full h-full object-contain pointer-events-none z-[5] rounded-[36px]"
          />
        )}

        <div className="relative z-10">
          <div className="pt-4 border-white/30">
            <div className="flex items-start justify-between gap-2 mb-3 pr-8">
              <div className=" text-2xl text-white">Manisha&rsquo;s World</div>
              {isOwn && (
                <span className=" text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/20 text-white border border-white/30 shrink-0 mt-1">
                  You
                </span>
              )}
            </div>
            <div className="text-[13px] text-white/85 space-y-1">
              <div><span className="text-white/50">VISITOR</span></div>
              <div className="uppercase font-bold text-[14px]">{visitor.name}</div>
              <div className="mt-2"><span className="text-white/50">ISSUED ON</span></div>
              <div className="text-[13px]">{formatDate(visitor.created_at)}</div>
              <div className="mt-3 text-[13px]">NO. {cardNo}</div>
            </div>
            <div className="flex items-center justify-end gap-1 text-white text-[12px]">
              <span style={{ fontSize: '15px', marginRight: '4px' }}>Sign here:</span>
              <div className="border-b border-white/60 w-40" />
            </div>
          </div>
        </div>
      </div>
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '10px 10px',
        }}
      />
    </div>
  );
}

export default function VisitorGalleryPage() {
  const [statsOpen, setStatsOpen] = useState(false);
  const [visitors, setVisitors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 12;
  const ownId = getOwnVisitorId();

  const load = async () => {
    setLoading(true);
    try {
      let list = await fetchVisitors();
      if (list.length === 0) {
        try { await seedVisitors(); } catch (_e) {}
        list = await fetchVisitors();
      }
      setVisitors(list);
      const s = await fetchVisitorStats();
      setStats(s);
    } catch (e) {
      console.error('Load failed', e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    try {
      await apiDelete(id);
      if (id === ownId) clearOwnVisitorId();
      await load();
    } catch (e) { console.error('Delete failed', e); }
  };

  return (
    <Layout>
      <div className="mx-auto">
        <div className="bg-cream-50 border border-cream-300/50 rounded-lg overflow-hidden">
          <button onClick={() => setStatsOpen(!statsOpen)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-cream-200/40 transition-colors">
            <span className=" text-[11px] uppercase tracking-wider text-inkwell-700">Stats for Nerds <span className="text-inkwell-400 ml-1">({stats?.total || 0})</span></span>
            {statsOpen ? <ChevronUp className="w-4 h-4 text-inkwell-700" /> : <ChevronDown className="w-4 h-4 text-inkwell-700" />}
          </button>
          {statsOpen && stats && (
            <div className="px-4 sm:px-5 pb-5 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
              <div className="col-span-2 md:col-span-2">
                <div className=" text-base text-inkwell-900 mb-2">Visitors Over Time</div>
                <Sparkline buckets={stats.daily_buckets} />
                <div className="flex justify-between text-[11px] text-inkwell-400 mt-1">
                  <span>7 days ago</span><span>Today</span>
                </div>
              </div>
              <div>
                <div className=" text-base text-inkwell-900 mb-2">Color Picks</div>
                <ul className="space-y-1.5">
                  {stats.color_picks.map((c) => (
                    <li key={c.name} className="flex items-center gap-2 text-[13px]">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} />
                      <span className="flex-1 text-inkwell-700">{c.name}</span>
                      <span className="text-inkwell-500">{c.pct}%</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-wider text-inkwell-500">Signed Their Card</div>
                <div className=" text-3xl text-inkwell-900 mt-1">{stats.signed_pct}%</div>
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-wider text-inkwell-500">Latest Guest</div>
                <div className=" text-3xl text-inkwell-900 mt-1">{stats.latest_rel}</div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className=" text-[11px] uppercase tracking-wider text-inkwell-500">{visitors.length} cards on the wall</p>
          <div className="flex items-center gap-2">
            {ownId && (
              <Link to="/onboarding?edit" className="px-3 sm:px-4 py-2 rounded-lg bg-cream-200 hover:bg-cream-300 text-inkwell-900 text-[10.5px] sm:text-[11px] uppercase tracking-wider transition-colors flex items-center gap-1.5"><Pencil className="w-3 h-3" /> Edit my card</Link>
            )}
            <Link to="/onboarding?from=gallery" className="btn-magnetic btn-sheen px-3 sm:px-4 py-2 rounded-lg bg-inkwell-900 text-white text-[10.5px] sm:text-[11px] uppercase tracking-wider hover:bg-inkwell-700 whitespace-nowrap">{ownId ? 'Sign another' : 'Sign the guestbook'} →</Link>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-inkwell-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            <span className=" text-[11px] uppercase tracking-wider">Loading visitors…</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
            {visitors.slice(0, page * pageSize).map((v, i) => (
              <ScrollReveal key={v.id} delay={(i % pageSize) * 50} y={20}>
                <VisitorCard visitor={v} isOwn={v.id === ownId} onDelete={() => handleDelete(v.id)} />
              </ScrollReveal>
            ))}
          </div>
        )}

        {page * pageSize < visitors.length && (
          <div className="flex justify-center mt-8">
            <button onClick={() => setPage(page + 1)} className="px-5 py-2 rounded-lg bg-cream-200 hover:bg-cream-300 text-[11px] uppercase tracking-wider text-inkwell-700 transition-colors">Load more</button>
          </div>
        )}
        <div className="h-10" />
      </div>
    </Layout>
  );
}
